import { IconWifi, IconWifiOff, IconRefresh } from '@tabler/icons-react';
import { useOutbox } from '../hooks/useOutbox';

/**
 * ConnectionStatusBadge — small top-bar pill that surfaces
 * online/offline + outbox state at a glance.
 *
 * Phase 1.7a. Renders:
 *   - Online + 0 pending  → nothing (don't clutter the bar)
 *   - Syncing             → blue "syncing • N pending"
 *   - Offline             → orange "offline • N queued"
 *   - Online but >0 pending → blue "sync pending" (rare)
 *
 * Cohesion rule 5: this is the ONE visible offline indicator.
 * Nowhere else has its own "are we online" UI.
 */
export default function ConnectionStatusBadge() {
  const { online, pendingCount, isSyncing } = useOutbox();

  // Common case: online, nothing queued — render nothing
  if (online && pendingCount === 0 && !isSyncing) return null;

  if (!online) {
    return (
      <span
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-warning-soft text-warning text-12 font-bold uppercase tracking-wider"
        title={`Offline${pendingCount > 0 ? ` — ${pendingCount} write${pendingCount === 1 ? '' : 's'} queued` : ''}`}
      >
        <IconWifiOff size={14} stroke={2.25} />
        Offline{pendingCount > 0 ? ` · ${pendingCount}` : ''}
      </span>
    );
  }

  // Online but with pending writes — usually sync-in-flight
  return (
    <span
      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-info-soft text-info text-12 font-bold uppercase tracking-wider"
      title={isSyncing ? `Syncing — ${pendingCount} pending` : `${pendingCount} pending — will retry`}
    >
      {isSyncing ? (
        <IconRefresh size={14} stroke={2.25} className="animate-spin" />
      ) : (
        <IconWifi size={14} stroke={2.25} />
      )}
      {isSyncing ? 'Syncing' : 'Sync pending'} · {pendingCount}
    </span>
  );
}
